import { Timeline, Card, Typography, Space, Tag, Collapse, theme } from 'antd';
import { 
  CheckCircleOutlined, 
  CloseCircleOutlined, 
  ClockCircleOutlined,
  UserOutlined,
  RobotOutlined
} from '@ant-design/icons';
import { format } from 'date-fns';

const { Text, Paragraph } = Typography;

const statusConfig = {
  completed: { color: 'green', icon: <CheckCircleOutlined /> },
  failed: { color: 'red', icon: <CloseCircleOutlined /> },
  pending: { color: 'blue', icon: <ClockCircleOutlined /> },
  skipped: { color: 'gray', icon: <ClockCircleOutlined /> },
};

const actorConfig = {
  Patient: { icon: <UserOutlined />, color: 'blue' },
  CSR: { icon: <UserOutlined />, color: 'cyan' },
  System: { icon: <RobotOutlined />, color: 'default' },
};

export function StepTimeline({ steps }) {
  const { token } = theme.useToken();

  const hasDetails = (step) => 
    step.metadata || step.request || step.response || step.errorCode;

  const timelineItems = steps.map((step) => {
    const config = statusConfig[step.status];
    const actor = actorConfig[step.actor];

    const collapseItems = [];
    
    if (step.metadata) {
      collapseItems.push({
        key: 'metadata',
        label: 'Metadata',
        children: (
          <pre style={{ 
            fontSize: 12, 
            background: token.colorBgTextHover, 
            padding: 8, 
            borderRadius: 4,
            overflow: 'auto',
            margin: 0,
          }}>
            {JSON.stringify(step.metadata, null, 2)}
          </pre>
        ),
      });
    }
    
    if (step.request) {
      collapseItems.push({
        key: 'request',
        label: 'Request',
        children: (
          <pre style={{ 
            fontSize: 12, 
            background: token.colorBgTextHover, 
            padding: 8, 
            borderRadius: 4,
            overflow: 'auto',
            margin: 0,
          }}>
            {JSON.stringify(step.request, null, 2)}
          </pre>
        ),
      });
    }
    
    if (step.response) {
      collapseItems.push({
        key: 'response',
        label: 'Response',
        children: (
          <pre style={{ 
            fontSize: 12, 
            background: token.colorBgTextHover, 
            padding: 8, 
            borderRadius: 4,
            overflow: 'auto',
            margin: 0,
          }}>
            {JSON.stringify(step.response, null, 2)}
          </pre>
        ),
      });
    }

    return {
      color: config?.color || '#0000',
      dot: config?.icon,
      children: (
        <Card 
          size="small" 
          style={{ 
            marginBottom: 0,
            borderColor: step.status === 'failed' ? token.colorErrorBorder : undefined,
            background: step.status === 'failed' ? token.colorErrorBg : undefined,
          }}
        >
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Space>
                <Text strong>{step.stepName}</Text>
                <Tag icon={actor?.icon} color={actor?.color || '#0000'}>{step.actor}</Tag>
              </Space>
              <Space>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {format(new Date(step.timestamp), 'HH:mm:ss')}
                </Text>
                {step.duration && (
                  <Tag>
                    {step.duration < 1000 
                      ? `${step.duration}ms` 
                      : `${(step.duration / 1000).toFixed(1)}s`}
                  </Tag>
                )}
              </Space>
            </div>
            
            <Paragraph type="secondary" style={{ margin: 0, fontSize: 13 }}>
              {step.description}
            </Paragraph>

            {step.errorCode && (
              <Card 
                size="small" 
                style={{ 
                  background: token.colorErrorBg, 
                  borderColor: token.colorErrorBorder 
                }}
              >
                <Text code style={{ color: token.colorError }}>{step.errorCode}</Text>
                <Paragraph style={{ margin: '4px 0 0', fontSize: 12, color: token.colorError }}>
                  {step.errorMessage}
                </Paragraph>
              </Card>
            )}

            {hasDetails(step) && collapseItems.length > 0 && (
              <Collapse 
                size="small" 
                items={collapseItems}
                style={{ marginTop: 8 }}
              />
            )}
          </Space>
        </Card>
      ),
    };
  });

  return <Timeline items={timelineItems} />;
}
